import type { PlaybackAudioTrack } from "@recital-master/shared";

export type WebMediaAudioTrack = {
  id?: string;
  label: string;
};

export function audioTrackDescriptorForId(
  descriptors: PlaybackAudioTrack[],
  preferredId: string | null
) {
  return preferredId
    ? descriptors.find((descriptor) => descriptor.id === preferredId) ?? null
    : null;
}

/** hls.js matches this before it selects and buffers the manifest's default. */
export function hlsAudioPreferenceForId(
  descriptors: PlaybackAudioTrack[],
  preferredId: string | null
) {
  const descriptor = audioTrackDescriptorForId(descriptors, preferredId);
  return descriptor
    ? {
        name: descriptor.label,
        lang: descriptor.language
      }
    : undefined;
}

/**
 * Preserve the selected audio rendition at native-HLS source load time.
 * The playback gateway uses the stable feed ID to rewrite only the master
 * playlist's DEFAULT/AUTOSELECT flags; the signed path and token are unchanged.
 * Do not use `descriptor.language`: Audience and Clean are both English in the
 * catalog even though the authored HLS renditions need distinct identifiers.
 */
export function webHlsUriWithAudioPreference(
  uri: string,
  descriptors: PlaybackAudioTrack[],
  preferredId: string | null
) {
  const descriptor = audioTrackDescriptorForId(descriptors, preferredId);
  try {
    const url = new URL(uri);
    if (descriptor) {
      url.searchParams.set("audio", descriptor.id);
    }
    return url.toString();
  } catch {
    return uri;
  }
}

/**
 * A hidden queue slot may already contain decoded audio from the preference
 * that was active when it was preloaded. Switching that engine in place is
 * not enough to guarantee that the old samples cannot become briefly audible
 * when the slot is promoted. Rebuild only the hidden successor; the active
 * slot must keep playing while its feed is switched live.
 */
export function shouldRepreparePreloadedAudioSlot({
  active,
  hasSource,
  trackAvailable
}: {
  active: boolean;
  hasSource: boolean;
  trackAvailable: boolean;
}) {
  return !active && hasSource && trackAvailable;
}

/**
 * Ingest guarantees that catalog rendition order matches HLS rendition order.
 * Video.js exposes hls.js's numeric indices while app UI uses catalog IDs.
 */
export function mediaAudioTrackIndexForLogicalId(
  descriptors: PlaybackAudioTrack[],
  mediaTracks: WebMediaAudioTrack[],
  logicalId: string | null
) {
  if (!logicalId) return -1;
  const descriptorIndex = descriptors.findIndex((descriptor) => descriptor.id === logicalId);
  if (descriptorIndex >= 0 && descriptorIndex < mediaTracks.length) return descriptorIndex;
  return mediaTracks.findIndex((track) => track.id === logicalId);
}

export function logicalAudioTrackIdForMediaIndex(
  descriptors: PlaybackAudioTrack[],
  mediaTracks: WebMediaAudioTrack[],
  index: number
) {
  return descriptors[index]?.id ?? mediaTracks[index]?.id ?? null;
}

export function mapWebAudioTrackState(
  mediaTracks: WebMediaAudioTrack[],
  selectedMediaId: string | null,
  descriptors: PlaybackAudioTrack[]
) {
  const available = mediaTracks.map((track, index) => ({
    id: descriptors[index]?.id ?? track.id ?? String(index),
    label: descriptors[index]?.label ?? track.label
  }));
  const selectedIndex = mediaTracks.findIndex((track) => track.id === selectedMediaId);
  return {
    available,
    selectedId:
      selectedIndex >= 0
        ? logicalAudioTrackIdForMediaIndex(descriptors, mediaTracks, selectedIndex)
        : null
  };
}

/**
 * Preserve a valid user preference while Safari/AirPlay still reports the
 * outgoing rendition during its asynchronous receiver handoff.
 */
export function reportedWebAudioTrackId(
  available: Array<{ id: string }>,
  selectedId: string | null,
  preferredId: string | null
) {
  return preferredId &&
    available.some((track) => track.id === preferredId)
    ? preferredId
    : selectedId;
}
